// @flow
import { decorate, observable, computed, action } from 'mobx';
import type { User } from 'gwu';
import Api from '../../api';
import languageStore from '../lang/LanguageStore';
import { defaultLanguageCode } from '../../lang';
import authStore from '../auth/AuthStore';
import ROLES from '../../constants/roles';
import { get } from 'lodash';
import Cookies from 'universal-cookie';

class UserStore {
  user: User | null = null;
  isFetchingUserProfile: boolean = true;
  isAuthenticated: boolean = false;
  ssoUrl: string;

  async setUserPofile(user: User) {
    this.isAuthenticated = true;
    this.user = user;
    authStore.start(); // start tracking auth session
    if (this.user.preferences) {
      const cookies = new Cookies();
      const langCode = cookies.get('langCode') || 'en';
      if (langCode !== 'en') {
        this.updatePreference('languageCode', langCode);
      }
      await languageStore.setLanguage(langCode);
    }
  }

  async login(username: string) {
    try {
      this.isFetchingUserProfile = true;
      const { data } = await Api.login(username);
      if (data !== undefined) {
        await this.setUserPofile(data);
      }
      this.isFetchingUserProfile = false;
    } catch (e) {
      this.isFetchingUserProfile = false;
      this.isAuthenticated = false;
    }
  }

  async logout() {
    try {
      await Api.logout();
      this.isAuthenticated = false;
      this.user = null;
      authStore.stop(); // stop tracking auth session
    } catch (e) {
      // TODO
    }
  }

  async fetchUserProfile() {
    if (this.user !== null) {
      return;
    }

    try {
      this.isFetchingUserProfile = true;
      const { status, data } = await Api.getUserProfile();
      if (status === 401) {
        this.isAuthenticated = false;
        this.ssoUrl = data.ssoUrl;
      } else {
        if (data !== undefined) {
          await this.setUserPofile(data);
        }
      }

      this.isFetchingUserProfile = false;
    }
    catch (e) {
      this.isFetchingUserProfile = false;
      this.isAuthenticated = false;
    }
  }

  async completeOnBoarding() {
    this.updatePreference('onboardingMode', false);
  }

  async reopenOnBoarding() {
    this.updatePreference('onboardingMode', true);
  }

  async updateLanguage(languageCode: string) {
    await this.updatePreference('languageCode', languageCode);
    await languageStore.setLanguage(languageCode);
  }

  async updateCountry(countryCode: string) {
    this.updatePreference('country', countryCode);
  }

  async updateRole(role: string) {
    this.updatePreference('selectedRole', role);
  }

  async updatePreference(preferenceProperty: string, value: any) {
    if (!this.user) {
      return; // TODO throw error?
    }

    try {
      const existingPreferences = this.user.preferences ? this.user.preferences : {};
      const updatePayload = {
        preferences: {
          ...existingPreferences,
          [preferenceProperty]: value
        }
      };

      if (this.isAnonymous) {
        this.user.preferences = updatePayload.preferences;
        const cookies = new Cookies();
        cookies.set('langCode', value, { path: '/' });
      } else {
        const { data } = await Api.updateUserProfile(updatePayload);
        this.user = data;
      }
    }
    catch (e) {
      // TODO handle error
    }
  }

  get isChampion() {
    return this.user
      && this.user.preferences
      && this.user.preferences.selectedRole
      && this.user.preferences.selectedRole === ROLES.CHAMPION;
  }

  get userLanguage() {
    const userLanguage = this.user.preferences.languageCode || defaultLanguageCode;
    return userLanguage;
  }

  get isManaged() {
    const employeeType = get(this.user, 'dlz2.profile.employeeType', 'Managed');
    const isManaged = employeeType === 'Managed';
    return isManaged;
  }

  get isAnonymous() {
    return window.allowAnonymous;
  }
}

decorate(UserStore, {
  user: observable,
  isFetchingUserProfile: observable,
  isAuthenticated: observable,
  ssoUrl: observable,
  fetchUserProfile: action,
  completeOnBoarding: action,
  reopenOnBoarding: action,
  updateLanguage: action,
  updateCountry: action,
  updateRole: action,
  login: action,
  logout: action,
  isChampion: computed,
  userLanguage: computed,
  isManaged: computed,
  isAnonymous: computed
});

export default new UserStore();



// WEBPACK FOOTER //
// ./src/modules/user/UserStore.js